<script setup lang="ts">
// A SectionSpark in the FOF row that zooms (Paolo 2026-11-03 17:0x / 18:1x). The cell shows the mini drawing; on a
// hover-capable device hovering it opens a popover with the full drawing (grid, labels, section %s), on touch a tap
// opens a bottom sheet with a backdrop and a close button (the tap never follows the row / card link). Moving a pointer
// or a finger across the zoomed chart shows a crosshair and reads the time (UK), the value and the move against the
// base. `mode` forces hover / tap for tests ('auto' asks `(hover: hover)`).
import { computed, onUnmounted, ref } from 'vue'
import SectionSpark from './SectionSpark.vue'

const props = withDefaults(defineProps<{
  values: number[]
  label: string
  valueText: string
  subText?: string
  upIsGood?: boolean
  closeAt?: number | null
  decisionAt?: number | null
  markAt?: number | null
  baseValue?: number | null
  baseLabel?: string
  markLabel?: string
  decisionLabel?: string
  stepFrom?: number | null
  stepTo?: number | null
  startUtc?: string | null
  endUtc?: string | null
  unit?: string
  dp?: number
  mode?: 'auto' | 'hover' | 'tap'
  decisionColour?: string | null
}>(), { subText: '', upIsGood: true, closeAt: null, decisionAt: null, markAt: null, baseValue: null, baseLabel: 'base', markLabel: 'strike',
  decisionLabel: '08:40', stepFrom: null, stepTo: null, startUtc: null, endUtc: null, unit: '', dp: 2, mode: 'auto', decisionColour: null })

const open = ref(false)
const sheet = ref(false)
const pos = ref({ left: 0, top: 0 })
const cursor = ref<number | null>(null)
const trigger = ref<HTMLElement | null>(null)
const POP_W = 620, POP_H = 330

function hoverable(): boolean {
  if (props.mode === 'hover') return true
  if (props.mode === 'tap') return false
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(hover: hover)').matches
}
function openAt(asSheet: boolean) {
  if (props.values.length < 2) return
  sheet.value = asSheet
  if (!asSheet && trigger.value) {
    const r = trigger.value.getBoundingClientRect()
    const vw = window.innerWidth || 1024, vh = window.innerHeight || 768
    const left = Math.max(8, Math.min(r.left, vw - POP_W - 8))
    const top = r.bottom + 8 + POP_H > vh ? Math.max(8, r.top - POP_H - 8) : r.bottom + 8
    pos.value = { left, top }
  }
  open.value = true
  cursor.value = null
  document.addEventListener('keydown', onKey)
}
function close() { open.value = false; cursor.value = null; document.removeEventListener('keydown', onKey) }
function onKey(e: KeyboardEvent) { if (e.key === 'Escape') close() }
function onEnter() { if (hoverable()) openAt(false) }
function onLeave() { if (!sheet.value) close() }
function onClick(e: MouseEvent) {
  e.preventDefault(); e.stopPropagation()
  if (hoverable()) { open.value ? close() : openAt(false); return }
  openAt(true)
}
function onMove(e: PointerEvent) {
  const el = e.currentTarget as HTMLElement
  const r = el.getBoundingClientRect()
  if (r.width <= 0 || props.values.length < 2) return
  // the zoom drawing's plot area is x ∈ [52, 586] of 600
  const frac = Math.max(0, Math.min(1, ((e.clientX - r.left) / r.width * 600 - 52) / 534))
  cursor.value = Math.round(frac * (props.values.length - 1))
}
onUnmounted(() => document.removeEventListener('keydown', onKey))

const n = computed(() => props.values.length)
const idx = computed(() => cursor.value ?? (n.value - 1))
const cursorX = computed(() => n.value < 2 ? 0 : ((52 + (idx.value / (n.value - 1)) * 534) / 600) * 100)
const value = computed(() => n.value ? props.values[idx.value] : null)
const base = computed(() => props.baseValue != null && props.baseValue > 0 ? props.baseValue : null)
const fmt = (v: number) => v.toLocaleString('en-GB', { minimumFractionDigits: props.dp, maximumFractionDigits: props.dp })
const signed = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(2)}%`
const timeText = computed(() => {
  if (!props.startUtc || !props.endUtc || n.value < 2) return ''
  const a = Date.parse(props.startUtc), b = Date.parse(props.endUtc)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return ''
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', weekday: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(a + (b - a) * idx.value / (n.value - 1))) + ' UK'
})
const vsBase = computed(() => value.value == null || base.value == null ? null : (value.value / base.value - 1) * 100)
const stepText = computed(() => props.stepFrom != null && props.stepTo != null
  ? `revalued at the ${props.markLabel}: ${fmt(props.stepFrom)} → ${fmt(props.stepTo)}${props.unit} (${signed((props.stepTo / props.stepFrom - 1) * 100)})` : '')
</script>

<template>
  <span ref="trigger" class="ssz" :class="{ open }" data-testid="section-spark-zoom" @pointerenter="onEnter" @pointerleave="onLeave" @click="onClick">
    <SectionSpark :values="values" size="mini" :up-is-good="upIsGood" :close-at="closeAt" :decision-at="decisionAt" :mark-at="markAt"
                  :base-value="baseValue" :step-from="stepFrom" :step-to="stepTo" :decision-colour="decisionColour" />
  </span>
  <Teleport to="body">
    <div v-if="open && sheet" class="ssz-backdrop" data-testid="section-spark-zoom-backdrop" @click="close"></div>
    <div v-if="open" class="ssz-panel" :class="sheet ? 'sheet' : 'pop'" :style="sheet ? undefined : { left: `${pos.left}px`, top: `${pos.top}px` }"
         data-testid="section-spark-zoom-panel" role="dialog" :aria-label="`${label} zoomed`" @pointerleave="onLeave" @click.stop>
      <div class="hd">
        <span class="lbl">{{ label }}</span>
        <span v-if="subText" class="sub">{{ subText }}</span>
        <span class="val" :class="valueText.startsWith('−') ? 'down' : valueText.startsWith('+') ? 'up' : ''">{{ valueText }}</span>
        <button v-if="sheet" type="button" class="x" aria-label="Close" data-testid="section-spark-zoom-close" @click="close">✕</button>
      </div>
      <div class="chart" data-testid="section-spark-zoom-chart" @pointermove="onMove" @pointerdown="onMove">
        <SectionSpark :values="values" size="zoom" :up-is-good="upIsGood" :close-at="closeAt" :decision-at="decisionAt" :mark-at="markAt"
                      :base-value="baseValue" :base-label="baseLabel" :mark-label="markLabel" :decision-label="decisionLabel"
                      :step-from="stepFrom" :step-to="stepTo" :start-utc="startUtc" :end-utc="endUtc" :unit="unit" :dp="dp" :decision-colour="decisionColour" />
        <div v-if="cursor !== null" class="xhair" :style="{ left: `${cursorX}%` }"></div>
      </div>
      <div class="ro" data-testid="section-spark-zoom-readout">
        <span class="t">{{ timeText || (cursor === null ? 'latest' : '') }}</span>
        <span class="v" v-if="value != null">{{ fmt(value) }}{{ unit }}</span>
        <span v-if="vsBase != null" class="d" :class="vsBase >= 0 ? 'up' : 'down'">{{ signed(vsBase) }} vs {{ baseLabel }} {{ fmt(base!) }}{{ unit }}</span>
      </div>
      <div v-if="stepText" class="step" data-testid="section-spark-zoom-step">{{ stepText }}</div>
      <div class="hint">{{ sheet ? 'Drag across the chart to read a point' : 'Move across the chart to read a point' }}</div>
    </div>
  </Teleport>
</template>

<style scoped>
.ssz { display: block; cursor: zoom-in; border-radius: 3px; }
.ssz.open { outline: 1px solid #6ea8df; }
.ssz-backdrop { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.45); z-index: 999; }
.ssz-panel { position: fixed; z-index: 1000; background: #1a1a1a; color: #e0e0e0; border: 1px solid #3a3a3a; border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5); padding: 10px 12px; display: grid; gap: 8px; }
.ssz-panel.pop { width: 620px; max-width: calc(100vw - 16px); }
.ssz-panel.sheet { left: 0; right: 0; bottom: 0; border-radius: 12px 12px 0 0;
  padding-left: max(16px, env(safe-area-inset-left, 0px)); padding-right: max(16px, env(safe-area-inset-right, 0px));
  padding-bottom: max(14px, env(safe-area-inset-bottom, 0px)); }
.hd { display: flex; align-items: center; gap: 10px; }
.hd .lbl { font-size: 11px; font-weight: 700; letter-spacing: .4px; color: #9e9e9e; text-transform: uppercase; }
.hd .sub { font-family: 'JetBrains Mono', Consolas, monospace; font-size: 11px; color: #9e9e9e; }
.hd .val { font-family: 'JetBrains Mono', Consolas, monospace; font-size: 15px; margin-left: auto; }
.hd .val.up { color: #4caf50; } .hd .val.down { color: #ef5350; }
.hd .x { background: none; border: 0; color: #9e9e9e; font-size: 16px; cursor: pointer; padding: 2px 6px; }
.chart { position: relative; touch-action: none; }
.sheet .chart :deep(svg.section-spark.zoom) { height: 250px; }
.xhair { position: absolute; top: 0; bottom: 0; width: 0; border-left: 1px dashed #bdbdbd; pointer-events: none; }
.ro { display: flex; flex-wrap: wrap; gap: 6px 14px; font-family: 'JetBrains Mono', Consolas, monospace; font-size: 12px; font-variant-numeric: tabular-nums; }
.ro .t { color: #9e9e9e; }
.ro .d.up { color: #4caf50; } .ro .d.down { color: #ef5350; }
.step { font-size: 11px; color: #ffb74d; }
.hint { font-size: 10px; color: #777; }
@media (max-width: 639px) { .ssz { cursor: pointer; } }
</style>
